import { User } from '@client/components/_ui/display/User';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useUser } from '@client/hooks/business/user/useUser';
import { trpc } from '@client/trpc/client';
import { SYSTEM_ACCOUNT_IDS } from '@shared/constants';
import { Select, type SelectProps, Spin, Tag } from 'antd';
import { type ReactNode, useMemo } from 'react';
import { match } from 'ts-pattern';
import { buildUserOptionGroups, renderMeOption } from './user-selector-options';

/** A selected account the caller can name when the fetched list cannot. */
export type RetainedUser = { userId: bigint; fullName: string; photoUrl: string | null };

type Props = {
    readonly?: boolean;
    placeholder?: string;
    /**
     * If true, only shows users that share handling parties with the current user.
     * handlingPartyIds will work as additional filter in this case
     */
    limitToCurrentUserHandlingParties?: boolean;
    handlingPartyIds?: number[] | bigint[];
    removeSystemAccount?: boolean;
    removeAnonymousAccount?: boolean;
    onlyEmployee?: boolean;
    showDisabledAccounts?: boolean;
    /**
     * Selected accounts that may be missing from the fetched list — typically
     * disabled accounts a saved record still targets while `showDisabledAccounts`
     * is off. Each one the result does not carry is shown by name, greyed and not
     * re-selectable, rather than as a bare id; the rest use their fetched option.
     */
    retainedUsers?: RetainedUser[];
    showUnallocated?: boolean;
    /** Show a "Current user" option that resolves to the viewer's id (for saved "My items" filters). */
    showCurrentUser?: boolean;
    className?: string;
    size?: 'x-small' | 'small' | 'medium' | 'large';
    maxTagCount?: 'responsive' | number;
    getPopupContainer?: SelectProps['getPopupContainer'];
    value?: bigint[] | null;
    onChange?: (
        userId: bigint[] | null,
        option: {
            value: bigint[] | null;
            label: ReactNode;
        },
    ) => void;
};

export function UserSelectorMultiple({
    value,
    onChange,
    handlingPartyIds,
    removeSystemAccount = true,
    removeAnonymousAccount = true,
    onlyEmployee,
    showUnallocated,
    showCurrentUser,
    limitToCurrentUserHandlingParties,
    showDisabledAccounts = true,
    retainedUsers,
    size = 'medium',
    maxTagCount = 'responsive',
    ...props
}: Props) {
    const currentUser = useUser();
    const { can, isSuperAdmin } = usePermissions();
    const canAdminOrg = can('org:admin');
    const { data, isPending } = trpc.user.listUsersForSelector.useQuery(
        {
            where: {
                AND: [
                    ...(limitToCurrentUserHandlingParties &&
                    !canAdminOrg &&
                    !isSuperAdmin &&
                    currentUser?.handlingPartyIds?.length
                        ? [
                              {
                                  HandlingParties: {
                                      some: {
                                          handlingPartyId: {
                                              in: currentUser.handlingPartyIds,
                                          },
                                      },
                                  },
                              },
                          ]
                        : []),
                    ...(handlingPartyIds?.length
                        ? [
                              {
                                  HandlingParties: {
                                      some: {
                                          handlingPartyId: {
                                              in: handlingPartyIds,
                                          },
                                      },
                                  },
                              },
                          ]
                        : []),
                    ...(removeSystemAccount
                        ? [
                              {
                                  userId: { not: SYSTEM_ACCOUNT_IDS.SYSTEM },
                                  isSupportAccount: { not: true },
                              },
                          ]
                        : []),
                    // The claims agent (CUR-1158) is never assignable, whatever the caller shows.
                    { userId: { not: SYSTEM_ACCOUNT_IDS.CLARA } },
                    ...(removeAnonymousAccount ? [{ userId: { not: SYSTEM_ACCOUNT_IDS.ANONYMOUS } }] : []),
                    ...(onlyEmployee ? [{ isEmployee: true }] : []),
                    ...(!showDisabledAccounts ? [{ disabled: { not: true } }] : []),
                ],
            },
        },
        {
            select({ rows }) {
                const map = rows.reduce(
                    (prev, item) => {
                        const key = item.fullName.toLowerCase() || item.email;
                        return { ...prev, [key]: prev[key] ? prev[key] + 1 : 1 };
                    },
                    {} as Record<string, number>,
                );

                const options = rows.map((item) => {
                    const key = item.fullName.toLowerCase() || item.email;
                    const name = map[key] > 1 ? `${item.fullName} (${item.email})` : item.fullName;

                    return {
                        value: item.userId,
                        label: (
                            <User
                                userId={item.userId}
                                photoUrl={item.photoUrl}
                                name={name}
                                showPopover={false}
                                size={size}
                            />
                        ),
                        searchValue: `${item.fullName} ${item.email} ${item.phone || ''}`.toLowerCase().trim(),
                    };
                });

                if (showUnallocated) {
                    options.unshift({
                        value: SYSTEM_ACCOUNT_IDS.UNALLOCATED,
                        label: <User size={size} showPopover={false} />,
                        searchValue: 'unallocated',
                    });
                }

                if (showCurrentUser) {
                    options.unshift({
                        value: SYSTEM_ACCOUNT_IDS.CURRENT_USER,
                        // Dynamic "Me" sentinel. A plain text label keeps the compact selected tag clean
                        // (matching the "Mine" handling-party tag and the applied filter chip); the
                        // distinctive accent icon-circle is added in the dropdown via `optionRender`.
                        label: <span>Me</span>,
                        searchValue: 'me current user',
                    });
                }

                return options;
            },
        },
    );

    const isLoading = isPending || !data;
    const flatOptions = useMemo(() => {
        if (!data || !retainedUsers?.length) return data;
        const fetched = new Set(data.map((option) => String(option.value)));
        const retained = retainedUsers
            .filter((user) => !fetched.has(String(user.userId)))
            .map((user) => ({
                value: user.userId,
                label: (
                    <User
                        userId={user.userId}
                        photoUrl={user.photoUrl}
                        name={user.fullName}
                        showPopover={false}
                        size={size}
                        disabled
                    />
                ),
                searchValue: user.fullName.toLowerCase(),
                disabled: true,
            }));
        return retained.length ? [...data, ...retained] : data;
    }, [data, retainedUsers, size]);
    const options = buildUserOptionGroups(flatOptions, showCurrentUser);

    return (
        <Select
            value={value}
            onChange={onChange as any}
            options={options}
            mode="multiple"
            showSearch={{
                filterOption: (input, option) => !!option?.searchValue?.includes(input.toLowerCase()),
            }}
            optionRender={(option) =>
                // The dynamic "Me" sentinel gets a distinct two-line card in the dropdown (its
                // plain-string label keeps the selected tag compact); every other option renders as-is.
                String(option.value) === String(SYSTEM_ACCOUNT_IDS.CURRENT_USER)
                    ? renderMeOption(currentUser.fullName)
                    : (option.label as ReactNode)
            }
            tagRender={
                isLoading
                    ? () => (
                          <Tag className="flex items-center gap-2 bg-transparent text-subtle-foreground">
                              <Spin size="small" />
                              Loading users...
                          </Tag>
                      )
                    : undefined
            }
            notFoundContent={
                isLoading ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-subtle-foreground">
                        <Spin size="small" />
                        Loading users...
                    </div>
                ) : undefined
            }
            popupMatchSelectWidth={false}
            popupClassName={showCurrentUser ? 'crm-grouped-filter-popup' : undefined}
            allowClear
            maxTagCount={maxTagCount}
            classNames={{ root: `crm-user-selector-multiple crm-user-selector-multiple-${size}` }}
            size={match(size)
                .with('x-small', 'small', () => 'small' as const)
                .with('medium', () => 'middle' as const)
                .with('large', () => 'large' as const)
                .exhaustive()}
            {...props}
        />
    );
}
